import { Box, Button, IconButton, Paper, Table, TableBody, TableContainer, TablePagination, TextField, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getTablePaginationCount } from 'src/utils';
import Iconify from '../Iconify';
import Scrollbar from '../Scrollbar';
import { ConfirmDeleteDialog } from '../confirm-dialog';
import SkeletonTable from '../skeleton/SkeletonTable';
import { TableHeadCustom, TableNoData, TableSelectedActions } from '../table';

const GeneralTableWithDragAndDrop = (props) => {
  const {
    className,
    tableContainerSX,
    tableData,
    rowID,
    headLabel,
    isLoading,
    isDeleteLoading,
    CustomTableRow,
    onEditRow,
    onDeleteRow,
    onDeleteRows,
    openConfirmDelete,
    setOpenConfirmDelete,
    useTableOptions,
    selectedRow,
    setSelectedRow,
    hasDeleteOption = true,
    isDraggable,
    onDragEnd,
    resetDragAndDrop,
    exportToExcel,
    showGoToPageInput = false,
    hasPagination = true,
    ...rest
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { page, setPage, onChangePage, order, orderBy, selected, rowsPerPage, onSelectAllRows, onSelectRow, onRowsPerPageChange } =
    useTableOptions;

  const [searchParams, setSearchParams] = useSearchParams();

  const [inputPage, setInputPage] = useState(Number(searchParams.get('page')) || 0);
  const [maxPage, setMaxPage] = useState();
  const totalTableRows = page * rowsPerPage + tableData?.length;

  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  const isNotFound = tableData.length === 0;

  const handleRedirect = () => {
    const newPage = Number(inputPage);
    if (totalTableRows % rowsPerPage === 0 || inputPage <= maxPage) {
      setPage(newPage);
      if (newPage === 0) navigate(location?.pathname);
      else navigate(`${location?.pathname}?page=${newPage}`);
    } else {
      enqueueSnackbar('شماره صفحه موردنظر وجود ندارد', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (totalTableRows) {
      const pageNumber = Number(searchParams.get('page')) || 0;
      setInputPage(pageNumber);
      if (totalTableRows % rowsPerPage !== 0) {
        setMaxPage(pageNumber);
      }
    }
  }, [totalTableRows, searchParams.get('page')]);

  return (
    <>
      <Scrollbar>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="table">
            {(provided) => (
              <TableContainer component={Paper} ref={provided.innerRef} {...provided.droppableProps} sx={tableContainerSX}>
                {hasDeleteOption && selected.length > 0 && (
                  <TableSelectedActions
                    numSelected={selected.length}
                    rowCount={tableData.length}
                    onSelectAllRows={(checked) =>
                      onSelectAllRows(
                        checked,
                        tableData.map((row) => row[rowID])
                      )
                    }
                    actions={
                      <>
                        {exportToExcel ? (
                          <Box sx={{ display: 'flex', gap: '3px' }}>
                            <Tooltip title=" دانلود فایل اکسل">
                              <IconButton color="inherit" sx={{ color: 'black' }} onClick={exportToExcel}>
                                <Iconify icon={'ri:file-excel-2-line'} width={24} height={24} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="حذف">
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setOpenConfirmDelete(true);
                                  setIsMultiSelectMode(true);
                                }}
                              >
                                <Iconify icon={'eva:trash-2-outline'} width={24} height={24} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Button
                            color="error"
                            variant="contained"
                            onClick={() => {
                              setOpenConfirmDelete(true);
                              setIsMultiSelectMode(true);
                            }}
                          >
                            حذف
                          </Button>
                        )}
                      </>
                    }
                  />
                )}

                <Table stickyHeader size="medium" className={className}>
                  <TableHeadCustom
                    order={order}
                    orderBy={orderBy}
                    headLabel={headLabel}
                    rowCount={tableData.length}
                    {...(hasDeleteOption > 0 && {
                      numSelected: selected.length,
                      onSelectAllRows: (checked) =>
                        onSelectAllRows(
                          checked,
                          tableData.map((row) => row[rowID])
                        ),
                    })}
                    isDraggable={isDraggable}
                    resetDragAndDrop={resetDragAndDrop}
                  />
                  <TableBody>
                    {isLoading
                      ? [...Array(7)]?.map((_, index) => <SkeletonTable key={index} numOfColumns={headLabel.length + 1} />)
                      : tableData?.map((row, index) => (
                          <Draggable key={row[rowID]} draggableId={row[rowID]?.toString()} isDragDisabled={!isDraggable} index={index}>
                            {(provided, snapshot) => (
                              <CustomTableRow
                                row={row}
                                key={row[rowID]}
                                rowNumber={page * rowsPerPage + index + 1}
                                selected={selected.includes(row[rowID])}
                                setSelectedRow={setSelectedRow}
                                onEditRow={() => onEditRow(row)}
                                onSelectRow={() => onSelectRow(row[rowID])}
                                onDeleteRow={() => {
                                  setOpenConfirmDelete(true);
                                  setSelectedRow(row);
                                }}
                                isDragging={snapshot.isDragging}
                                isDraggable={isDraggable}
                                items={tableData}
                                index={index}
                                ref={isDraggable ? provided.innerRef : null}
                                {...(isDraggable ? provided.draggableProps : {})}
                                {...(isDraggable ? provided.dragHandleProps : {})}
                                {...rest}
                              />
                            )}
                          </Draggable>
                        ))}
                    <TableNoData isNotFound={!isLoading && isNotFound} />
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Droppable>
        </DragDropContext>
      </Scrollbar>

      {!isLoading && tableData?.length > 0 && hasPagination ? (
        <div className="flex items-center justify-between gap-4" style={{ borderTop: '1px solid rgba(0, 0, 0, 0.1)', paddingTop: '10px' }}>
          {showGoToPageInput ? (
            <form onSubmit={handleRedirect}>
              <div className="flex items-center gap-2 mr-5 my-4">
                <TextField
                  label="شماره صفحه"
                  type="number"
                  value={inputPage}
                  onChange={(e) => setInputPage(e.target.value)}
                  size="small"
                  sx={{ width: '100px' }}
                  inputProps={{ style: { textAlign: 'center' }, min: 0 }}
                />
                <Button type="submit" variant="contained" disabled={Number(inputPage) > maxPage}>
                  رفتن به صفحه
                </Button>
              </div>
            </form>
          ) : null}

          <TablePagination
            rowsPerPageOptions={[rowsPerPage]}
            component="div"
            count={getTablePaginationCount(page, rowsPerPage, tableData?.length)}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onChangePage}
            onRowsPerPageChange={onRowsPerPageChange}
            sx={{ width: '100%', borderTop: 'none' }}
          />
        </div>
      ) : null}

      <ConfirmDeleteDialog
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        confirmDelete={() => {
          if (selected?.length && isMultiSelectMode) {
            onDeleteRows(selected);
          } else {
            onDeleteRow(selectedRow[rowID]);
          }
        }}
        title={`آیا برای حذف ${selected?.length > 1 && isMultiSelectMode ? 'آیتم های' : 'آیتم'} موردنظر اطمینان دارید؟`}
        isLoading={isDeleteLoading}
      />
    </>
  );
};

export default GeneralTableWithDragAndDrop;
